import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { tourApi } from "@/lib/tourEntities";
import { apiError } from "@/lib/errors";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpCircle, ChevronDown, ChevronRight, Eye, UserPlus } from "lucide-react";
import { CustomerFormDialog } from "@/pages/customers/CustomerFormDialog";
import { useAuth } from "@/contexts/AuthContext";
import { LinkedContactPicker } from "@/components/LinkedContactPicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const _footerPreviewApi = axios.create({ baseURL: `${BACKEND}/api` });
_footerPreviewApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("aa_ops_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUSES = ["inquiry", "hold", "confirmed", "on_hold", "active_tour", "completed", "billing", "closed", "cancelled"];

const Section = ({ title, children }) => (
  <fieldset className="border border-coach-silver rounded-sm p-3 mb-3">
    <legend className="px-1 text-[10.5px] font-semibold uppercase tracking-widest text-steel">{title}</legend>
    <div className="space-y-2">{children}</div>
  </fieldset>
);

const Field = ({ label, children, hint, tooltip, required }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1 flex items-center gap-1">
      {label} {required && <span className="text-critical-red">*</span>}
      {tooltip && (
        <span title={tooltip} className="inline-flex items-center">
          <HelpCircle size={11} className="text-steel" />
        </span>
      )}
    </div>
    {children}
    {hint && <div className="mt-1 text-[10.5px] text-steel">{hint}</div>}
  </label>
);

const inp = "w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white";

/**
 * Simplified & progressive Tour form.
 * Minimum required: customer_id + display_name. Status defaults to `inquiry`.
 * Dates and everything else are optional — collapse behind "More Details".
 * lockCustomer=true keeps the customer selector read-only (used when
 * launched from Customer 360).
 */
export const TourFormDialog = ({ open, customers, onClose, onSaved, initial, lockCustomer = false }) => {
  const nav = useNavigate();
  const { can } = useAuth();
  const isEdit = !!initial?.id;
  // When editing, default to open More Details so users see the rest of the fields.
  // On create, start collapsed so minimum fields dominate.
  const [moreOpen, setMoreOpen] = useState(isEdit);
  // Quote Quick Create — Add New Client inline (P1 owner gate, Wave 3 Phase A).
  // We keep locally-created clients in `localCustomers` so a freshly-created
  // record appears in the selector immediately, even before the parent
  // page's master list refetches. Preserves all in-progress Tour form data
  // across open/save/cancel because CustomerFormDialog is a sibling modal
  // — nothing in `form` state is touched.
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [localCustomers, setLocalCustomers] = useState([]);
  const canCreateCustomer = can && can("customers.create");
  const mergedCustomers = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of [...localCustomers, ...(customers || [])]) {
      if (!c?.id || seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [customers, localCustomers]);

  const [form, setForm] = useState({
    // Minimum
    customer_id: initial?.customer_id || "",
    display_name: initial?.display_name || "",
    status: initial?.status || "inquiry",
    // Common-optional
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    next_departure_date: initial?.next_departure_date || "",
    notes: initial?.notes || "",
    artist_display: initial?.artist_display || "",
    legal_entity_organization_id: initial?.legal_entity_organization_id || "",
    // Dates & Locations
    pickup_datetime: initial?.pickup?.datetime || "",
    pickup_location: initial?.pickup?.location || "",
    pickup_address: initial?.pickup?.address || "",
    pickup_contact_name: initial?.pickup?.contact_name || "",
    pickup_contact_phone: initial?.pickup?.contact_phone || "",
    pickup_contact: initial?.pickup?.contact || null,
    pickup_contact_id: initial?.pickup?.contact_id || null,
    dropoff_datetime: initial?.dropoff?.datetime || "",
    dropoff_location: initial?.dropoff?.location || "",
    dropoff_address: initial?.dropoff?.address || "",
    dropoff_contact_name: initial?.dropoff?.contact_name || "",
    dropoff_contact_phone: initial?.dropoff?.contact_phone || "",
    dropoff_contact: initial?.dropoff?.contact || null,
    dropoff_contact_id: initial?.dropoff?.contact_id || null,
    // Tour Basis
    estimated_mileage: initial?.estimated_mileage ?? "",
    actual_mileage: initial?.actual_mileage ?? "",
    tour_days: initial?.tour_days ?? "",
    billed_days: initial?.billed_days ?? "",
    discounted_days: initial?.discounted_days ?? "",
    billed_months: initial?.billed_months ?? "",
    bus_deadhead_days: initial?.bus_deadhead_days ?? "",
    bus_deadhead_pct: initial?.bus_deadhead_pct ?? "",
    driver_deadhead_days: initial?.driver_deadhead_days ?? "",
    driver_deadhead_pct: initial?.driver_deadhead_pct ?? "",
    tour_basis_bus_dhf: initial?.tour_basis?.bus_dhf ?? "",
    tour_basis_bus_dhr: initial?.tour_basis?.bus_dhr ?? "",
    tour_basis_driver_dhf: initial?.tour_basis?.driver_dhf ?? "",
    tour_basis_driver_dhr: initial?.tour_basis?.driver_dhr ?? "",
    tour_basis_total_miles_estimated: initial?.tour_basis?.total_miles_estimated ?? "",
    quote_footer_text: initial?.quote_footer_text || "",
    // Front Office Data Reconciliation Wave — starter enums.
    contract_type: initial?.contract_type || "",
    rate_type: initial?.rate_type || "",
  });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Tour name is required");
    if (!form.customer_id) return toast.error("Client is required");
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      return toast.error("Start date must be on or before end date");
    }
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        customer_id: form.customer_id || null,
        display_name: form.display_name.trim(),
        artist_display: form.artist_display || null,
        legal_entity_organization_id: form.legal_entity_organization_id || null,
        status: form.status || "inquiry",
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        next_departure_date: form.next_departure_date || null,
        pickup: (form.pickup_datetime || form.pickup_location || form.pickup_address || form.pickup_contact_id || form.pickup_contact_name || form.pickup_contact_phone) ? {
          datetime: form.pickup_datetime || null,
          location: form.pickup_location || null,
          address: form.pickup_address || null,
          contact_name: form.pickup_contact_name || null,
          contact_phone: form.pickup_contact_phone || null,
          contact_id: form.pickup_contact_id || null,
        } : null,
        dropoff: (form.dropoff_datetime || form.dropoff_location || form.dropoff_address || form.dropoff_contact_id || form.dropoff_contact_name || form.dropoff_contact_phone) ? {
          datetime: form.dropoff_datetime || null,
          location: form.dropoff_location || null,
          address: form.dropoff_address || null,
          contact_name: form.dropoff_contact_name || null,
          contact_phone: form.dropoff_contact_phone || null,
          contact_id: form.dropoff_contact_id || null,
        } : null,
        estimated_mileage: num(form.estimated_mileage),
        actual_mileage: num(form.actual_mileage),
        tour_days: num(form.tour_days),
        billed_days: num(form.billed_days),
        discounted_days: num(form.discounted_days),
        billed_months: num(form.billed_months),
        bus_deadhead_days: num(form.bus_deadhead_days),
        bus_deadhead_pct: num(form.bus_deadhead_pct),
        driver_deadhead_days: num(form.driver_deadhead_days),
        driver_deadhead_pct: num(form.driver_deadhead_pct),
        tour_basis: {
          bus_dhf: num(form.tour_basis_bus_dhf),
          bus_dhr: num(form.tour_basis_bus_dhr),
          driver_dhf: num(form.tour_basis_driver_dhf),
          driver_dhr: num(form.tour_basis_driver_dhr),
          total_miles_estimated: num(form.tour_basis_total_miles_estimated),
        },
        notes: form.notes || null,
        quote_footer_text: form.quote_footer_text || null,
        contract_type: form.contract_type || null,
        rate_type: form.rate_type || null,
      };
      if (isEdit) {
        await tourApi.patch(initial.id, payload);
        toast.success("Tour updated");
        onSaved();
      } else {
        const created = await tourApi.create(payload);
        toast.success("Tour created");
        onSaved(created);
        if (created?.id) { nav(`/tours/${created.id}`); return; }
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const dhfTip = "Legacy StarTracker field — business definition pending confirmation with All Access.";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 flex flex-col" data-testid="tour-form-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display">{isEdit ? "Edit Tour" : "New Tour"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="text-[13px] px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <Section title="Tour Information">
            <Field label="Client" required>
              {lockCustomer ? (
                <input
                  data-testid="tf-customer-locked"
                  disabled
                  value={
                    (mergedCustomers.find((c) => c.id === form.customer_id)?.display_name) ||
                    form.customer_id
                  }
                  className={`${inp} text-steel bg-road-white`}
                />
              ) : (
                <div className="flex gap-2 items-stretch">
                  <select data-testid="tf-customer" value={form.customer_id}
                    onChange={(e) => setField("customer_id", e.target.value)}
                    className={`${inp} flex-1 min-w-0`}>
                    <option value="">
                      {mergedCustomers.length === 0 ? "— No clients found —" : "— Choose client —"}
                    </option>
                    {form.customer_id && !mergedCustomers.some((c) => c.id === form.customer_id) && (
                      <option value={form.customer_id}>
                        Current client (id: {form.customer_id.slice(0, 8)}…)
                      </option>
                    )}
                    {mergedCustomers.map((c) => <option key={c.id} value={c.id}>{`${c.display_name}${c.customer_number ? ` (${c.customer_number})` : ""}`}</option>)}
                  </select>
                  {canCreateCustomer && (
                    <button
                      type="button"
                      onClick={() => setAddClientOpen(true)}
                      className="inline-flex items-center gap-1 border border-coach-blue text-coach-blue bg-white hover:bg-coach-blue/5 rounded-sm px-2.5 text-[12.5px] font-medium whitespace-nowrap"
                      data-testid="tf-add-client">
                      <UserPlus size={13} /> Add New Client
                    </button>
                  )}
                </div>
              )}
              {!lockCustomer && canCreateCustomer && mergedCustomers.length === 0 && (
                <div className="mt-1 text-[11px] text-steel">
                  No clients found — use{" "}
                  <button type="button" onClick={() => setAddClientOpen(true)}
                    className="text-coach-blue hover:underline">Add New Client</button>{" "}
                  to create one without leaving this form.
                </div>
              )}
            </Field>
            <Field label="Tour Name" required>
              <input data-testid="tf-name" value={form.display_name}
                onChange={(e) => setField("display_name", e.target.value)}
                autoFocus
                placeholder="e.g. Fall Arena Run 2026"
                className={inp} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Start Date">
                <input data-testid="tf-start" type="date" value={form.start_date}
                  onChange={(e) => setField("start_date", e.target.value)} className={inp} />
              </Field>
              <Field label="End Date">
                <input data-testid="tf-end" type="date" value={form.end_date}
                  onChange={(e) => setField("end_date", e.target.value)} className={inp} />
              </Field>
            </div>
            <Field label="Next Date Out" hint="Optional. Appears on the Driver Pickup Sheet.">
              <input data-testid="tf-next-date-out" type="date" value={form.next_departure_date}
                onChange={(e) => setField("next_departure_date", e.target.value)} className={inp} />
            </Field>
            <Field label="Status">
              <select data-testid="tf-status" value={form.status}
                onChange={(e) => setField("status", e.target.value)} className={inp}>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <Field label="Notes" hint="Anything memorable about this tour — you can add details later.">
              <textarea data-testid="tf-notes" rows={2} value={form.notes}
                onChange={(e) => setField("notes", e.target.value)} className={inp} />
            </Field>
          </Section>

          <button
            type="button"
            data-testid="tf-more-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="mb-2 flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
          >
            {moreOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            More Details {moreOpen ? "" : "(artist, pickup/dropoff, tour basis, quote footer)"}
          </button>

          {moreOpen && (
            <div data-testid="tf-more-details">
              <Section title="Artist & Legal">
                <Field label="Artist / Show Display">
                  <input data-testid="tf-artist" value={form.artist_display}
                    onChange={(e) => setField("artist_display", e.target.value)} className={inp} />
                </Field>
              </Section>

              <Section title="Pickup & Dropoff">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Pickup Date/Time">
                    <input data-testid="tf-pickup-dt" type="datetime-local" value={form.pickup_datetime}
                      onChange={(e) => setField("pickup_datetime", e.target.value)} className={inp} />
                  </Field>
                  <Field label="Pickup Location">
                    <input data-testid="tf-pickup-loc" value={form.pickup_location}
                      onChange={(e) => setField("pickup_location", e.target.value)} className={inp} />
                  </Field>
                </div>
                <Field label="Pickup Address">
                  <input data-testid="tf-pickup-addr" value={form.pickup_address}
                    onChange={(e) => setField("pickup_address", e.target.value)} className={inp} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Pickup Contact Name (legacy)" hint="Manual entry preserved when no Canonical Contact is linked.">
                    <input data-testid="tf-pickup-contact-name" value={form.pickup_contact_name}
                      onChange={(e) => setField("pickup_contact_name", e.target.value)} className={inp} />
                  </Field>
                  <Field label="Pickup Contact Phone (legacy)">
                    <input data-testid="tf-pickup-contact-phone" value={form.pickup_contact_phone}
                      onChange={(e) => setField("pickup_contact_phone", e.target.value)} className={inp} />
                  </Field>
                </div>
                <div data-testid="tf-pickup-contact-picker-wrap">
                  <LinkedContactPicker
                    label="Pickup — Linked Contact (optional)"
                    testidPrefix="tf-pickup-contact"
                    customerId={form.customer_id}
                    value={form.pickup_contact_id ? (form.pickup_contact || {
                      id: form.pickup_contact_id,
                      first_name: form.pickup_contact_name,
                    }) : null}
                    onChange={(c) => {
                      setField("pickup_contact_id", c?.id || null);
                      setField("pickup_contact", c);
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Drop-Off Date/Time">
                    <input data-testid="tf-dropoff-dt" type="datetime-local" value={form.dropoff_datetime}
                      onChange={(e) => setField("dropoff_datetime", e.target.value)} className={inp} />
                  </Field>
                  <Field label="Drop-Off Location">
                    <input data-testid="tf-dropoff-loc" value={form.dropoff_location}
                      onChange={(e) => setField("dropoff_location", e.target.value)} className={inp} />
                  </Field>
                </div>
                <Field label="Drop-Off Address">
                  <input data-testid="tf-dropoff-addr" value={form.dropoff_address}
                    onChange={(e) => setField("dropoff_address", e.target.value)} className={inp} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Drop-Off Contact Name (legacy)" hint="Manual entry preserved when no Canonical Contact is linked.">
                    <input data-testid="tf-dropoff-contact-name" value={form.dropoff_contact_name}
                      onChange={(e) => setField("dropoff_contact_name", e.target.value)} className={inp} />
                  </Field>
                  <Field label="Drop-Off Contact Phone (legacy)">
                    <input data-testid="tf-dropoff-contact-phone" value={form.dropoff_contact_phone}
                      onChange={(e) => setField("dropoff_contact_phone", e.target.value)} className={inp} />
                  </Field>
                </div>
                <div data-testid="tf-dropoff-contact-picker-wrap">
                  <LinkedContactPicker
                    label="Drop-Off — Linked Contact (optional)"
                    testidPrefix="tf-dropoff-contact"
                    customerId={form.customer_id}
                    value={form.dropoff_contact_id ? (form.dropoff_contact || {
                      id: form.dropoff_contact_id,
                      first_name: form.dropoff_contact_name,
                    }) : null}
                    onChange={(c) => {
                      setField("dropoff_contact_id", c?.id || null);
                      setField("dropoff_contact", c);
                    }}
                  />
                </div>
              </Section>

              <Section title="Tour Basis">
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Tour Days"><input data-testid="tf-tour-days" type="number" min="0" value={form.tour_days}
                    onChange={(e) => setField("tour_days", e.target.value)} className={inp} /></Field>
                  <Field label="Billed Days"><input data-testid="tf-billed-days" type="number" min="0" value={form.billed_days}
                    onChange={(e) => setField("billed_days", e.target.value)} className={inp} /></Field>
                  <Field label="Discounted Days"><input data-testid="tf-disc-days" type="number" min="0" value={form.discounted_days}
                    onChange={(e) => setField("discounted_days", e.target.value)} className={inp} /></Field>
                  <Field label="Billed Months"><input data-testid="tf-billed-months" type="number" step="0.01" min="0" value={form.billed_months}
                    onChange={(e) => setField("billed_months", e.target.value)} className={inp} /></Field>
                  <Field label="Est. Mileage"><input data-testid="tf-est-mileage" type="number" min="0" value={form.estimated_mileage}
                    onChange={(e) => setField("estimated_mileage", e.target.value)} className={inp} /></Field>
                  <Field label="Actual Mileage"><input data-testid="tf-actual-mileage" type="number" min="0" value={form.actual_mileage}
                    onChange={(e) => setField("actual_mileage", e.target.value)} className={inp} /></Field>
                  <Field label="Bus DH Days"><input data-testid="tf-bus-dh-days" type="number" min="0" value={form.bus_deadhead_days}
                    onChange={(e) => setField("bus_deadhead_days", e.target.value)} className={inp} /></Field>
                  <Field label="Bus DH %"><input data-testid="tf-bus-dh-pct" type="number" step="0.01" min="0" value={form.bus_deadhead_pct}
                    onChange={(e) => setField("bus_deadhead_pct", e.target.value)} className={inp} /></Field>
                  <Field label="Driver DH Days"><input data-testid="tf-drv-dh-days" type="number" min="0" value={form.driver_deadhead_days}
                    onChange={(e) => setField("driver_deadhead_days", e.target.value)} className={inp} /></Field>
                  <Field label="Driver DH %"><input data-testid="tf-drv-dh-pct" type="number" step="0.01" min="0" value={form.driver_deadhead_pct}
                    onChange={(e) => setField("driver_deadhead_pct", e.target.value)} className={inp} /></Field>
                  <Field label="Est. Total Miles">
                    <input data-testid="tf-tb-total-miles" type="number" min="0" value={form.tour_basis_total_miles_estimated}
                      onChange={(e) => setField("tour_basis_total_miles_estimated", e.target.value)} className={inp} />
                  </Field>
                </div>
                <div className="mt-2 pt-2 border-t border-coach-silver">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Legacy StarTracker Fields</div>
                  <div className="grid grid-cols-4 gap-2">
                    <Field label="Bus DHF" tooltip={dhfTip}><input data-testid="tf-bus-dhf" type="number" step="0.01" value={form.tour_basis_bus_dhf}
                      onChange={(e) => setField("tour_basis_bus_dhf", e.target.value)} className={inp} /></Field>
                    <Field label="Bus DHR" tooltip={dhfTip}><input data-testid="tf-bus-dhr" type="number" step="0.01" value={form.tour_basis_bus_dhr}
                      onChange={(e) => setField("tour_basis_bus_dhr", e.target.value)} className={inp} /></Field>
                    <Field label="Driver DHF" tooltip={dhfTip}><input data-testid="tf-drv-dhf" type="number" step="0.01" value={form.tour_basis_driver_dhf}
                      onChange={(e) => setField("tour_basis_driver_dhf", e.target.value)} className={inp} /></Field>
                    <Field label="Driver DHR" tooltip={dhfTip}><input data-testid="tf-drv-dhr" type="number" step="0.01" value={form.tour_basis_driver_dhr}
                      onChange={(e) => setField("tour_basis_driver_dhr", e.target.value)} className={inp} /></Field>
                  </div>
                </div>
              </Section>

              <Section title="Quote Footer">
                <Field label="Footer Text" hint="Appears at the bottom of generated quote PDFs. Overrides the org-level template.">
                  <textarea data-testid="tf-quote-footer" rows={2} value={form.quote_footer_text}
                    onChange={(e) => setField("quote_footer_text", e.target.value)} className={inp} />
                </Field>
                {isEdit && initial?.id && (
                  <QuoteFooterPreview
                    tourId={initial.id}
                    overrideText={form.quote_footer_text}
                    contractType={form.contract_type}
                  />
                )}
              </Section>

              <Section title="Contract & Rate Basis">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Contract Type" hint="Starter enum — owner clarification pending. Nullable.">
                    <select
                      data-testid="tf-contract-type"
                      value={form.contract_type}
                      onChange={(e) => setField("contract_type", e.target.value)}
                      className={inp}
                    >
                      <option value="">— Unspecified —</option>
                      {["all_inclusive", "non_all_inclusive", "other"].map((t) => (
                        <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Rate Type" hint="Starter enum — owner clarification pending. Nullable.">
                    <select
                      data-testid="tf-rate-type"
                      value={form.rate_type}
                      onChange={(e) => setField("rate_type", e.target.value)}
                      className={inp}
                    >
                      <option value="">— Unspecified —</option>
                      {["daily", "weekly", "monthly", "flat", "other"].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="text-[10.5px] text-steel">
                  Non-all-inclusive quotes use the org-level Non-All-Inclusive footer template unless a tour override is set above.
                </div>
              </Section>
            </div>
          )}

          {!isEdit && (
            <div className="text-[11px] text-steel mb-2">
              Enter what you know now. Coaches, drivers, contacts, and pricing can all be added later.
            </div>
          )}

          <DialogFooter className="pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12.5px] text-steel border border-coach-silver rounded-sm">Cancel</button>
            <button data-testid="tf-submit" disabled={saving}
              className="px-4 py-1.5 text-[12.5px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white rounded-sm disabled:opacity-60">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Tour"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
      {/* Quote Quick Create — inline Add New Client (Wave 3 Phase A).
          The sibling modal preserves all TourFormDialog state because it
          lives in a separate React tree slot; the parent form is untouched
          across open/save/cancel/failure. On save we auto-select the new
          client id and merge it into the local selector list so the user
          never has to leave this Tour form. */}
      <CustomerFormDialog
        open={addClientOpen}
        orgs={[]}
        onClose={() => setAddClientOpen(false)}
        onSaved={(saved) => {
          if (saved?.id) {
            setLocalCustomers((prev) => [saved, ...prev]);
            setField("customer_id", saved.id);
          }
          setAddClientOpen(false);
        }}
      />
    </Dialog>
  );
};


const SOURCE_LABEL = {
  tour_override: "Tour Override",
  standard_template: "Standard Template",
  non_all_inclusive_template: "Non-All-Inclusive Template",
  empty: "No Footer",
};

/**
 * QuoteFooterPreview — resolver preview panel.
 * Shows the EXACT footer text that will be captured when a NEW quote version
 * is generated from this tour. Reads from `/api/reports/_footer-preview/{tourId}`.
 * The preview refetches whenever the local override or contract_type changes
 * so previews stay in sync with in-flight edits.
 *
 * IMPORTANT: this panel is READ-ONLY. It does NOT mutate the tour, org
 * templates, or any existing quote snapshot.
 */
const QuoteFooterPreview = ({ tourId, overrideText, contractType }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localSource, setLocalSource] = useState(null);
  const [localText, setLocalText] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await _footerPreviewApi.get(`/reports/_footer-preview/${tourId}`);
      setPreview(r.data);
    } catch {
      setPreview({ error: true });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (tourId) load(); /* eslint-disable-next-line */ }, [tourId]);

  // Live-compute the preview when the user edits the override text or
  // contract_type inline. This is a client-side approximation of the
  // server resolver so the preview reflects unsaved edits without a
  // round-trip.
  useEffect(() => {
    if (!preview) return;
    const trimmed = (overrideText || "").trim();
    if (trimmed) {
      setLocalSource("tour_override");
      setLocalText(trimmed);
    } else {
      // Server-side template pieces are on `preview.resolved_footer_text`
      // only when the tour's OWN override is empty; if the user just cleared
      // the override we don't have the template pre-fetched separately, so
      // we fall back to "reload from server on next open" and display the
      // server value for the current server-side contract_type.
      setLocalSource(null);
      setLocalText(null);
    }
  }, [overrideText, contractType, preview]);

  if (!tourId) return null;
  const displayText = localText != null ? localText : (preview?.resolved_footer_text || "");
  const displaySource = localSource || preview?.source || "empty";
  return (
    <div
      data-testid="quote-footer-preview"
      className="mt-2 rounded-sm border border-coach-silver bg-road-white px-3 py-2 text-[11.5px]"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="inline-flex items-center gap-1 uppercase tracking-widest text-[10px] text-steel">
          <Eye size={11} /> Preview at generation
        </span>
        <span
          data-testid="quote-footer-preview-source"
          className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-coach-silver bg-white text-carbon"
        >
          {SOURCE_LABEL[displaySource] || displaySource}
        </span>
      </div>
      {loading ? (
        <div className="text-steel">Resolving…</div>
      ) : displayText ? (
        <div data-testid="quote-footer-preview-text" className="whitespace-pre-wrap text-carbon">{displayText}</div>
      ) : (
        <div data-testid="quote-footer-preview-text" className="text-steel italic">— No footer will be captured —</div>
      )}
      <div className="mt-1 text-[10px] text-steel">
        Preview only. Does not mutate the tour, org templates, or existing quote snapshots.
      </div>
    </div>
  );
};
